import React from 'react';
import styled from 'styled-components';
import { pluralize } from 'shared-utils/utils/formatString';
import { formatSimpleDate, formatStandardDateLocaleString } from 'shared-utils/utils/formatDate';
import { createTrackingProps } from 'shared-utils/analytics';
import { SearchField } from './Input';
import ChevronDown from 'design-system/src/assets/icons/chevronDown.svg?react';
import Checkmark from 'design-system/src/assets/icons/checkmarkThin.svg?react';
import { focusVisibleStyles, standardTransitionStyles } from 'design-system/src/sharedStyles';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import TabList from 'design-system/src/components/TabList';
import { TableDropdownFilter } from 'design-system/src/components/TableDropdownFilter/TableDropdownFilter';
import { Checkbox } from 'design-system/src/components/Checkbox/Checkbox';
import { breakpoints } from 'design-system/src/tokens/breakpoints';

export const DEFAULT_COLUMN_SIZE = 140;
export const DATE_COLUMN_SIZE = 100;

export const TableContainer = styled.div`
  font-size: 0.9rem;
  color: var(--grayscale-600);
  height: 100%;
  box-sizing: border-box;
`;

export const FilterBar = styled.div<{ $mobileBreakpoint?: number }>`
  display: flex;
  padding: 12px 0;
  gap: 12px;
  border-bottom: 1px solid var(--grayscale-100);
  flex-direction: column;

  @media (max-width: ${({ $mobileBreakpoint = breakpoints.smallDesktopMax }) => $mobileBreakpoint}px) {
    gap: 0;
  }
`;

export const FilterBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  row-gap: 8px;
`;

/**
 * Banner displayed when search results are being shown.
 * Shows "Search results for "<searchTerm>"" text with a "Clear search" button and result count.
 * Optionally accepts children to render additional elements (e.g., download button) after the count.
 */
export const SearchResultsBanner: React.FC<{
  searchTerm: string;
  onClearSearch: () => void;
  count: number;
  children?: React.ReactNode;
}> = ({ searchTerm, onClearSearch, count, children }) => (
  <FilterBarRow className="w-full justify-between">
    <div className="flex items-center gap-2">
      <Typography styledAs="bodyExtraSmallSpaceGrotesk" className="flex h-8 items-center">
        Search results for "
        <Typography styledAs="bodyExtraSmallSpaceGrotesk" renderedAs="span" weight={500}>
          {searchTerm}
        </Typography>
        "
      </Typography>
      <Button size="xs" variant="secondary" onClick={onClearSearch}>
        Clear search
      </Button>
    </div>

    <TableResultCount count={count}>{children}</TableResultCount>
  </FilterBarRow>
);

export const FilterBarRowSection = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  gap: 1rem;

  @media (max-width: ${breakpoints.smallDesktopMax}px) {
    order: 1;
  }
`;

// TODO(WIT-6591): justify-content is misspelled
export const BulkActionsRow = styled(FilterBarRow)`
  justfiy-content: space-between;
`;

export const DividerPipe = styled.div`
  width: 1px;
  height: 24px;
  background-color: var(--grayscale-100);
  margin: 0 8px;
`;

export const ToggleSelectionCheckbox = styled.button<{ $checked: boolean }>`
  all: unset;
  display: flex;
  width: 18px;
  height: 18px;
  color: var(--${({ $checked }) => ($checked ? 'purple' : 'medium-gray')});

  &:disabled {
    color: var(--grayscale-50);
    cursor: not-allowed;
  }
`;

export const Table = styled.table`
  box-sizing: border-box;
  width: 100%;
  padding: 0 1.25rem;
  border-collapse: collapse;
  font-size: 1rem;
`;

export const TableHeader = styled(Typography).attrs({
  renderedAs: 'th',
  styledAs: 'bodyExtraSmallSpaceGrotesk',
  weight: 500,
})<{ $width?: string; $sortable?: boolean }>`
  box-sizing: border-box;
  padding: 0.625rem;
  border: none;
  text-align: left;
  border-bottom: 1px solid var(--grayscale-100);
  white-space: nowrap;
  ${({ $width }) => ($width ? `width: ${$width}px;` : '')};
  color: var(--grayscale-900);
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
`;

export const TableCell = styled(Typography).attrs({
  renderedAs: 'td',
  styledAs: 'bodySmallDMSans',
})<{ $width?: string; $verticalAlign?: 'top' | 'middle' | 'bottom' }>`
  vertical-align: ${({ $verticalAlign }) => $verticalAlign || 'top'};
  padding: 0.625rem;
  border: none;
  color: var(--grayscale-900);
  box-sizing: border-box;
  ${({ $width }) => ($width ? `width: ${$width}px;` : '')};
  ${({ $width }) => ($width ? `max-width: ${$width}px;` : '')};
`;

export const CenteredTableCell = styled(TableCell)`
  text-align: center;
  padding: 2rem;
`;

export const SearchInput = styled(SearchField).attrs({
  inputSize: 'sm',
})<{
  $width?: string;
  ref?: React.ForwardedRef<HTMLInputElement>;
}>`
  ${({ $width = '280px' }) => `width: ${$width};`}
`;

export const Row = styled.tr`
  border-bottom: 1px solid var(--grayscale-100);
`;

// TODO: turn into a proper component that applies the onClick handler to onKeyDown
export const ClickableRow = styled(Row).attrs({ tabIndex: 0 })<{ $selected?: boolean; $new?: boolean }>`
  cursor: pointer;
  ${standardTransitionStyles}

  &:hover,
  &:focus-visible {
    background-color: var(--grayscale-50);
  }

  // child TableCell
  & > td {
    ${({ $new }) => ($new ? 'font-weight: bold;' : '')}
  }

  ${({ $selected }) =>
    $selected &&
    `
    // TODO(tokens): Replace legacy color token --extra-light-gray with numbered equivalent once available.
    background-color: var(--extra-light-gray);
  `}

  ${focusVisibleStyles}
`;

export const OfficeNameTableDropdownFilter = styled(TableDropdownFilter)`
  // Office names are especially long
  width: 22.5rem;
`;

const SortIndicatorIcon = styled(ChevronDown)<{ $desc: boolean }>`
  color: var(--grayscale-900);
  transform: rotate(${({ $desc }) => ($desc ? 0 : 180)}deg);
  transition: transform 0.2s ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// This container makes sure there is always the same amount of space to the right
// of the header text, regardless of whether the sorting indicator is present, to
// avoid the column width changing when the sorting indicator appears/disappears.
const IconContainer = styled.div`
  width: 1rem;
  display: inline-flex;
  justify-content: flex-end;
  align-items: center;
`;

export type HeaderSortingIndicatorProps = {
  // Typescript says this can also be a string, but I don't know
  // what string what would be...
  isSorted: boolean | string;
  sortDirection: 'asc' | 'desc' | false;
};

/**
 * Up/down chevron for headers of sorted table columns.
 */
export const HeaderSortingIndicator = ({ isSorted, sortDirection }: HeaderSortingIndicatorProps) => {
  return (
    <IconContainer>
      {isSorted ? (
        <SortIndicatorIcon
          $desc={sortDirection === 'desc'}
          title={`sorted ${sortDirection === 'desc' ? 'descending' : 'ascending'}`}
        />
      ) : null}
    </IconContainer>
  );
};

export const TableTabList = styled(TabList).attrs({
  variant: 'fullWidthWithBorder',
})``;

export const TabsAndSearchRow = styled.div<{ $mobileBreakpoint?: number }>`
  display: flex;
  align-items: flex-end;

  @media (max-width: ${({ $mobileBreakpoint = breakpoints.smallDesktopMax }) => $mobileBreakpoint}px) {
    flex-direction: column-reverse;
    align-items: stretch;
    margin-bottom: 12px;
  }
`;

export const TabsAndSearchRowSearch = styled(SearchField).attrs({
  inputSize: 'sm',
})`
  width: 420px;
  margin-left: auto;
  padding-bottom: 4px;
  // bottom border below the search input that mimics the bottom border of the tabs
  border-bottom: 1px solid var(--grayscale-100);

  @media (max-width: ${breakpoints.smallDesktopMax}px) {
    width: 100%;
    margin-left: 0;
    margin-bottom: 2px;
    border: none;
  }
`;

export const ResultCountContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
  align-items: center;
`;

export const ResultCount = styled(Typography).attrs({
  renderedAs: 'span',
  styledAs: 'bodyExtraSmallSpaceGrotesk',
  color: 'var(--grayscale-900)',
})`
  white-space: nowrap;
`;

interface TableResultCountProps {
  count: number;
  children?: React.ReactNode;
}

export const TableResultCount: React.FC<TableResultCountProps> = ({ count, children }) => (
  <ResultCountContainer {...createTrackingProps({ unmask: true })}>
    <ResultCount>
      {count === 0 ? 'No' : count} {pluralize(count, 'result')}
    </ResultCount>
    {children}
  </ResultCountContainer>
);

export const NoPaddingCheckbox = styled(Checkbox)`
  // compensate for the 9px padding so the checkbox doesn't cause the row to be taller or wider than necessary
  margin: -9px !important;
`;

export type DateCellWithStatusProps = {
  /**
   * The raw date string (ISO format) to format and display.
   * Formatted using standard format (MM/DD/YYYY).
   */
  date: string;
  /**
   * Optional status label to display below the date (e.g., "Closed", "Reviewed").
   * When provided, shows the status indicator with a checkmark.
   */
  statusLabel?: string;
};

/**
 * A date cell component that optionally shows a status indicator (e.g., "Closed" with checkmark).
 * Used in table date columns when search results include items from completed/closed tabs.
 */
export const DateCellWithStatus: React.FC<DateCellWithStatusProps> = ({ date, statusLabel }) => {
  const formattedDate = formatStandardDateLocaleString(date);
  return (
    <div className="flex flex-col">
      <Typography styledAs="bodySmallSpaceGrotesk">{formattedDate}</Typography>
      {statusLabel && (
        <span className="flex items-center gap-1">
          <Typography
            styledAs="bodyExtraSmallDMSans"
            renderedAs="span"
            color="var(--grayscale-400)"
            className="!italic"
          >
            {statusLabel}
          </Typography>
          <Checkmark className="size-3 text-grayscale-400" aria-hidden />
        </span>
      )}
    </div>
  );
};

export type PreviewDateWithStatusProps = {
  /**
   * The raw date string (ISO format) to format and display.
   * Formatted using simple format (M/D/YY).
   */
  date: string;
  /**
   * Optional status label to display below the date (e.g., "Closed", "Reviewed").
   * When provided, shows the status indicator with a checkmark and dims the date color.
   */
  statusLabel?: string;
};

/**
 * A date component for search preview items that optionally shows a status indicator.
 * When statusLabel is provided, the date color dims to grayscale-400.
 * Used in the right column of search preview dropdown items.
 */
export const PreviewDateWithStatus: React.FC<PreviewDateWithStatusProps> = ({ date, statusLabel }) => {
  const formattedDate = formatSimpleDate(date);
  return (
    <div className="ml-4 flex shrink-0 flex-col items-end">
      <Typography
        styledAs="bodySmallSpaceGrotesk"
        renderedAs="div"
        color={statusLabel ? 'var(--grayscale-400)' : 'var(--grayscale-900)'}
      >
        {formattedDate}
      </Typography>
      {statusLabel && (
        <span className="flex items-center gap-1">
          <Typography
            styledAs="bodyExtraSmallDMSans"
            renderedAs="span"
            color="var(--grayscale-400)"
            className="!italic"
          >
            {statusLabel}
          </Typography>
          <Checkmark className="size-3 text-grayscale-400" aria-hidden />
        </span>
      )}
    </div>
  );
};
